import React, { useMemo, useState } from "react";
import { enqueueSnackbar } from "notistack";
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import GroupsIcon from '@mui/icons-material/Groups';
import SailingIcon from '@mui/icons-material/Sailing';
import TerrainIcon from '@mui/icons-material/Terrain';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { NuevoEstructuraAgentesForm } from './NuevoEstructuraAgentesForm';


const dataApi = [{
    sEstructuraAgentesCobranza: "A3649ED0-4417-F111-8AAF-000C29AF1C02",
    Periodo: "2026-03-31",
    Descripcion: "ESTRUCTURA DE AGENTES DE COBRANZA SEGÚN NECESIDADES DE CANTIDAD DE CREDITOS Y ZONAS",
    CreditosActivos: 100000,
    CreditosExternos: 40000,
    CreditosInternos: 60000,
},
{
    sEstructuraAgentesCobranza: "B3649ED0-4417-F111-8AAF-000C29AF1C02",
    Periodo: "2026-06-30",
    Descripcion: "ESTRUCTURA DE AGENTES DE COBRANZA SEGÚN NECESIDADES DE CANTIDAD DE CREDITOS Y ZONAS",
    CreditosActivos: 120000,
    CreditosExternos: 50000,
    CreditosInternos: 70000,
}
];

const porcentajeZona = {
    costa: 30,
    sierra: 60,
    oriente: 10,
};

const regionesDashboard = [
    { key: 'costa', nombre: 'Costa', porcentaje: porcentajeZona.costa, color: '#2563eb', icono: SailingIcon },
    { key: 'sierra', nombre: 'Sierra', porcentaje: porcentajeZona.sierra, color: '#7c3aed', icono: TerrainIcon },
    { key: 'oriente', nombre: 'Oriente', porcentaje: porcentajeZona.oriente, color: '#f59e0b', icono: TravelExploreIcon },
];

const formatoNumero = (valor) =>
    new Intl.NumberFormat("es-EC", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(valor || 0);

export function EstructuraAgentesCobranza() {
    const [registros, setRegistros] = useState(dataApi);
    const [periodoSeleccionado, setPeriodoSeleccionado] = useState('');
    const [mostrarFormularioNuevo, setMostrarFormularioNuevo] = useState(false);
    const valorFechaPeriodo = periodoSeleccionado ? `${periodoSeleccionado}-01` : '';

    const handlePeriodoChange = (e) => {
        setPeriodoSeleccionado(e.target.value);
    };

    const handlePeriodoDateChange = (e) => {
        const valorFecha = e.target.value;
        const periodo = valorFecha ? valorFecha.slice(0, 7) : '';
        handlePeriodoChange({ target: { value: periodo } });
    };

    const formatearPeriodoMesAnio = (periodo) => {
        if (!periodo) return 'Sin período seleccionado';
        const fecha = new Date(`${periodo}-01T00:00:00`);
        if (Number.isNaN(fecha.getTime())) return 'Sin período seleccionado';
        return fecha.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
    };

    const dataFiltrada = useMemo(() => {
        if (!periodoSeleccionado) return registros;
        return registros.filter((item) => String(item.Periodo).slice(0, 7) === periodoSeleccionado);
    }, [periodoSeleccionado, registros]);

    const registroSeleccionado = dataFiltrada[0] || null;
    const creditosInternos = Number(registroSeleccionado?.CreditosInternos) || 0;

    const regionesInternos = useMemo(() => {
        return regionesDashboard.map((region) => ({
            ...region,
            valor: (creditosInternos * region.porcentaje) / 100,
        }));
    }, [creditosInternos]);

    const ringSize = 110;
    const ringStroke = 10;
    const ringRadius = (ringSize - ringStroke) / 2;
    const ringCircunferencia = 2 * Math.PI * ringRadius;
    const calcularOffset = (porcentaje) => ringCircunferencia - (porcentaje / 100) * ringCircunferencia;

    const handleGuardarNuevo = ({ periodo, observacion }) => {
        const nuevoRegistro = {
            sEstructuraAgentesCobranza: `TEMP-${Date.now()}`,
            Periodo: `${periodo}-01`,
            Descripcion: "ESTRUCTURA DE AGENTES DE COBRANZA SEGÚN NECESIDADES DE CANTIDAD DE CREDITOS Y ZONAS",
            CreditosActivos: 0,
            CreditosExternos: 0,
            CreditosInternos: 0,
            Observacion: observacion,
        };

        setRegistros((prev) => [nuevoRegistro, ...prev]);
        setPeriodoSeleccionado(periodo);
        setMostrarFormularioNuevo(false);
        enqueueSnackbar('Registro creado correctamente.', { variant: 'success' });
    };

    if (mostrarFormularioNuevo) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 py-10 px-4 sm:px-6 lg:px-8">
                <div className="max-w-[900px] mx-auto">
                    <NuevoEstructuraAgentesForm
                        onCancel={() => setMostrarFormularioNuevo(false)}
                        onSave={handleGuardarNuevo}
                    />
                </div>
            </div>
        );
    }

    return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 py-10 px-4 sm:px-6 lg:px-8">
                <div className="max-w-[1400px] mx-auto">
                    <div className="bg-white border border-gray-200 rounded-2xl shadow-lg overflow-hidden">
                        <div className="px-6 py-5 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
                            <div className="flex flex-col md:flex-row items-center justify-between gap-3">
                                <h1 className="text-lg md:text-xl font-bold text-blue-700 uppercase tracking-wide text-center md:text-left">
                                    {registroSeleccionado?.Descripcion || 'ESTRUCTURA DE AGENTES DE COBRANZA'}
                                </h1>
                                <button
                                    onClick={() => setMostrarFormularioNuevo(true)}
                                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-indigo-600 to-blue-600 text-white font-semibold text-sm hover:from-indigo-700 hover:to-blue-700 transition-all shadow"
                                >
                                    <AddCircleOutlineIcon sx={{ fontSize: 18 }} /> Nuevo
                                </button>
                            </div>
                            <div className="mt-3 text-xs md:text-sm text-gray-600 text-center">
                                <span className="font-semibold">ID:</span> {registroSeleccionado?.sEstructuraAgentesCobranza || '--'} • {" "}
                                <span className="font-semibold">Período:</span> {registroSeleccionado?.Periodo || '--'}
                            </div>

                            <div className="mt-4 max-w-sm mx-auto">
                                <label className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1 justify-center">
                                    <CalendarMonthIcon sx={{ fontSize: 18 }} /> Filtrar por Período (Año-Mes)
                                </label>
                                <input
                                    type="date"
                                    value={valorFechaPeriodo}
                                    onChange={handlePeriodoDateChange}
                                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:border-purple-500 focus:ring-purple-200 transition-all bg-white hover:border-gray-300 cursor-pointer"
                                />
                                <p className="mt-2 text-xs text-gray-500 text-center capitalize">
                                    Período seleccionado: {formatearPeriodoMesAnio(periodoSeleccionado)}
                                </p>
                            </div>
                        </div>

                        <div className="px-6 py-6 bg-gray-50 border-t border-gray-200">
                            {!registroSeleccionado ? (
                                <div className="bg-white rounded-xl border border-gray-200 px-4 py-8 text-center text-gray-500 font-medium">
                                    No hay datos para el período seleccionado.
                                </div>
                            ) : (
                                <>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                                    <div className="bg-white rounded-xl border border-blue-200 p-5 shadow-sm">
                                        <div className="flex items-start justify-between">
                                            <div>
                                                <p className="text-xs text-blue-600 font-semibold uppercase">No. Créditos Totales</p>
                                                <p className="text-3xl font-bold text-blue-700 mt-1">{formatoNumero(registroSeleccionado.CreditosActivos)}</p>
                                            </div>
                                            <div className="p-2 rounded-lg bg-blue-100">
                                                <AccountBalanceWalletIcon className="text-blue-700" sx={{ fontSize: 20 }} />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="bg-white rounded-xl border border-purple-200 p-5 shadow-sm">
                                        <div className="flex items-start justify-between">
                                            <div>
                                                <p className="text-xs text-purple-600 font-semibold uppercase">No. de Créditos Gestionados por Externos</p>
                                                <p className="text-3xl font-bold text-purple-700 mt-1">{formatoNumero(registroSeleccionado.CreditosExternos)}</p>
                                            </div>
                                            <div className="p-2 rounded-lg bg-purple-100">
                                                <SupportAgentIcon className="text-purple-700" sx={{ fontSize: 20 }} />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="bg-white rounded-xl border border-emerald-200 p-5 shadow-sm">
                                        <div className="flex items-start justify-between">
                                            <div>
                                                <p className="text-xs text-emerald-600 font-semibold uppercase">No. Créditos Gestionados por Internos</p>
                                                <p className="text-3xl font-bold text-emerald-700 mt-1">{formatoNumero(registroSeleccionado.CreditosInternos)}</p>
                                            </div>
                                            <div className="p-2 rounded-lg bg-emerald-100">
                                                <GroupsIcon className="text-emerald-700" sx={{ fontSize: 20 }} />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-white rounded-xl border border-gray-200 p-5">
                                    <h2 className="text-sm md:text-base font-bold text-gray-700 text-center mb-1">
                                        DISTRIBUCIÓN REGIONAL DESDE CRÉDITOS INTERNOS
                                    </h2>
                                    <p className="text-xs text-gray-500 text-center mb-4">
                                        Base de cálculo: {formatoNumero(creditosInternos)} créditos internos
                                    </p>

                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                                        {regionesInternos.map((region) => {
                                            const IconoRegion = region.icono;
                                            return (
                                                <div key={region.key} className="rounded-xl border border-gray-200 p-4 flex flex-col items-center bg-gray-50">
                                                    <div className="relative" style={{ width: ringSize, height: ringSize }}>
                                                        <svg width={ringSize} height={ringSize} className="-rotate-90">
                                                            <circle
                                                                cx={ringSize / 2}
                                                                cy={ringSize / 2}
                                                                r={ringRadius}
                                                                stroke="#e5e7eb"
                                                                strokeWidth={ringStroke}
                                                                fill="none"
                                                            />
                                                            <circle
                                                                cx={ringSize / 2}
                                                                cy={ringSize / 2}
                                                                r={ringRadius}
                                                                stroke={region.color}
                                                                strokeWidth={ringStroke}
                                                                fill="none"
                                                                strokeLinecap="round"
                                                                strokeDasharray={ringCircunferencia}
                                                                strokeDashoffset={calcularOffset(region.porcentaje)}
                                                            />
                                                        </svg>
                                                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                                                            <span className="text-xl font-bold" style={{ color: region.color }}>{region.porcentaje}%</span>
                                                            <span className="text-xs text-gray-500">{region.nombre}</span>
                                                        </div>
                                                    </div>
                                                    <div className="mt-3 flex items-center gap-1" style={{ color: region.color }}>
                                                        <IconoRegion sx={{ fontSize: 18 }} />
                                                        <span className="text-sm font-semibold">{region.nombre}</span>
                                                    </div>
                                                    <p className="text-lg font-bold text-gray-800 mt-1">{formatoNumero(region.valor)}</p>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
    );
}
